import * as L from 'leaflet';
import { LatLngBoundsExpression, LatLngExpression } from 'leaflet';
import 'leaflet-defaulticon-compatibility';
import 'leaflet-defaulticon-compatibility/dist/leaflet-defaulticon-compatibility.css';
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';
import Box from '@mui/material/Box';
import Link from '@mui/material/Link';
import Typography from '@mui/material/Typography';
import { ReactNode, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import { MapContainer, Marker, Polyline, Popup, TileLayer, useMap } from 'react-leaflet';

import FullscreenControl from 'src/common/components/leaflet/FullscreenControl';
import MapWheelZoomHandler from 'src/common/components/leaflet/MapWheelZoomHandler';
import { LanguageKeys } from 'src/common/constants/languages';
import { showCookiePreferences } from 'src/cookieConsent/cookieConsentApi';
import useThirdPartyMapsConsent from 'src/cookieConsent/useThirdPartyMapsConsent';
import { getPathForLanguage } from 'src/helpers/publicPageUtils';
import usePublicPagesStore from 'src/stores/publicPagesStore';

import 'leaflet/dist/leaflet.css';

/** Inset when fitting the map to bounds (px). Smaller = tighter zoom on the track. */
const FIT_BOUNDS_PADDING: [number, number] = [12, 12];

function boundsSignature(bounds?: LatLngBoundsExpression): string {
  if (!bounds) return '';
  return JSON.stringify(bounds);
}

interface Props {
  bounds?: LatLngBoundsExpression;
  center?: LatLngExpression;
  children?: ReactNode;
  height?: string;
  markers?: {
    position: LatLngExpression;
    popupContent?: string;
  }[];
  polyLine?: LatLngExpression[] | LatLngExpression[][];
  scrollWheelZoom?: boolean;
  width?: string;
  zoom?: number;
}

function BoundsUpdater({ bounds }: { bounds?: LatLngBoundsExpression }) {
  const map = useMap();
  const lastFitSignatureRef = useRef('');

  useEffect(() => {
    if (!bounds) return;
    const signature = boundsSignature(bounds);
    if (signature === lastFitSignatureRef.current) return;
    lastFitSignatureRef.current = signature;
    map.fitBounds(bounds, { padding: FIT_BOUNDS_PADDING });
  }, [map, bounds]);

  return null;
}

function FullscreenBoundsUpdater({ bounds }: { bounds?: LatLngBoundsExpression }) {
  const map = useMap();

  useEffect(() => {
    const applyBounds = () => {
      map.invalidateSize();
      window.requestAnimationFrame(() => {
        if (bounds) {
          map.fitBounds(bounds, { padding: FIT_BOUNDS_PADDING });
        }
      });
    };

    const handleFullscreenChange = () => applyBounds();
    const handleBrowserFullscreenChange = () => applyBounds();

    map.on('fullscreenchange', handleFullscreenChange);
    map.on('enterFullscreen', handleFullscreenChange);
    map.on('exitFullscreen', handleFullscreenChange);
    document.addEventListener('fullscreenchange', handleBrowserFullscreenChange);

    return () => {
      map.off('fullscreenchange', handleFullscreenChange);
      map.off('enterFullscreen', handleFullscreenChange);
      map.off('exitFullscreen', handleFullscreenChange);
      document.removeEventListener('fullscreenchange', handleBrowserFullscreenChange);
    };
  }, [map, bounds]);

  return null;
}

function MapTilesPlaceholder({ height, width }: { height: string; width: string }) {
  const { t, i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage ?? 'hu') as LanguageKeys;
  const privacyPage = usePublicPagesStore(s => s.publicPagesByKey?.privacyPolicy);
  const publicPagesById = usePublicPagesStore(s => s.publicPagesById);
  const privacyHref = privacyPage
    ? getPathForLanguage(privacyPage, lang, publicPagesById)
    : '/privacy-policy';

  return (
    <Box
      sx={{
        height,
        width,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        bgcolor: 'grey.200',
        borderRadius: 1,
        px: 2,
        textAlign: 'center',
      }}
    >
      <Typography variant="body2" color="text.secondary">
        {t('cookieConsent.mapPlaceholder')}{' '}
        <Link component="button" type="button" variant="body2" onClick={() => showCookiePreferences()}>
          {t('footer.privacyPreferences')}
        </Link>
        {' · '}
        <Link component={RouterLink} to={privacyHref} variant="body2">
          {t('cookieConsent.privacyPolicyLink')}
        </Link>
      </Typography>
    </Box>
  );
}

const LeafletMap = ({
  bounds,
  center,
  children,
  width = '100%',
  height = '300px',
  markers,
  polyLine,
  scrollWheelZoom,
  zoom,
}: Props) => {
  const mapsConsent = useThirdPartyMapsConsent();

  const DefaultIcon = L.icon({
    iconUrl: icon,
    shadowUrl: iconShadow,
  });
  L.Marker.prototype.options.icon = DefaultIcon;

  if (!mapsConsent) {
    return <MapTilesPlaceholder height={height} width={width} />;
  }

  return (
    <MapContainer
      center={center}
      bounds={bounds}
      style={{
        height,
        width,
      }}
      scrollWheelZoom={scrollWheelZoom ?? true}
      zoom={zoom}
    >
      {scrollWheelZoom !== false ? <MapWheelZoomHandler /> : null}
      <BoundsUpdater bounds={bounds} />
      <FullscreenBoundsUpdater bounds={bounds} />
      <TileLayer
        attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
        url="https://tile.openstreetmap.org/{z}/{x}/{y}.png"
        maxZoom={19}
      />
      <FullscreenControl />
      {markers?.map((marker, i) => (
        <Marker key={i} position={marker.position}>
          {marker.popupContent && <Popup>{marker.popupContent}</Popup>}
        </Marker>
      ))}
      {polyLine?.length && (
        <Polyline pathOptions={{ fillColor: 'red', color: 'blue' }} positions={polyLine} />
      )}
      {children}
    </MapContainer>
  );
};

export default LeafletMap;
